// AdminConsistencyPage — Per-day profit % contribution validator
// 40% eval / 50% funded — flag accounts that exceed per-day limit
// Shows daily breakdown bar chart per trader

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Trader {
  id: string;
  name: string;
  avatar: number;
  account: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  accountSize: number;
  target: number;            // 6% of account
  phase: 'eval' | 'funded';
  days: { date: string; pnl: number }[];
  status: 'safe' | 'caution' | 'breach';
}

const TRADERS: Trader[] = [
  {
    id: 't1', name: 'Marcus J.', avatar: 1, account: 'FP-100K-14283', tier: 'Mid', accountSize: 100000, target: 6000, phase: 'funded',
    days: [
      { date: '07-22', pnl: 420 },
      { date: '07-23', pnl: 1840 },
      { date: '07-24', pnl: 920 },
      { date: '07-25', pnl: 640 },
      { date: '07-26', pnl: 1480 },
    ],
    status: 'safe',
  },
  {
    id: 't2', name: 'Sarah C.', avatar: 2, account: 'FP-150K-12844', tier: 'Professional', accountSize: 150000, target: 9000, phase: 'eval',
    days: [
      { date: '07-22', pnl: 0 },
      { date: '07-23', pnl: 5200 },  // 86% of target — BREACH
      { date: '07-24', pnl: 1200 },
      { date: '07-25', pnl: 480 },
      { date: '07-26', pnl: 980 },
    ],
    status: 'breach',
  },
  {
    id: 't3', name: 'Riley T.', avatar: 3, account: 'FP-50K-15201', tier: 'Beginner', accountSize: 50000, target: 3000, phase: 'eval',
    days: [
      { date: '07-22', pnl: 240 },
      { date: '07-23', pnl: 380 },
      { date: '07-24', pnl: 620 },
      { date: '07-25', pnl: 460 },
      { date: '07-26', pnl: 320 },
    ],
    status: 'safe',
  },
  {
    id: 't4', name: 'Devon K.', avatar: 4, account: 'FP-100K-13892', tier: 'Mid', accountSize: 100000, target: 6000, phase: 'funded',
    days: [
      { date: '07-22', pnl: 1240 },
      { date: '07-23', pnl: 3280 },  // 54.6% of target — breach (limit 50% funded)
      { date: '07-24', pnl: 480 },
      { date: '07-25', pnl: -120 },
      { date: '07-26', pnl: 240 },
    ],
    status: 'breach',
  },
];

const TIER_COLOR = { Beginner: '#34C759', Mid: '#0A84FF', Professional: '#1C1C1E' };

export function AdminConsistencyPage() {
  const nav = useNavigate();
  const [traders] = useState(TRADERS);
  const [filter, setFilter] = useState<'all' | 'breach' | 'safe'>('all');

  const filtered = traders.filter((t) => filter === 'all' || t.status === filter);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Consistency</div>
        <div className="w-9" />
      </div>

      {/* Rule explainer */}
      <div className="mx-4 mt-3">
        <div className="rounded-[14px] border border-white/10 bg-gradient-to-br from-[#FF9500] to-[#FF3B30] p-4 text-white">
          <div className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.85)' }}>Consistency rule</div>
          <div className="mt-1 text-[18px] font-bold">40% eval · 50% funded</div>
          <p className="text-[11px] mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>If daily profit exceeds the limit, excess is added to your target at 16:00 CT.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="mx-4 mt-3 grid grid-cols-3 gap-2">
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Tracked</div>
          <div className="text-[16px] font-bold">{traders.length}</div>
        </div>
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Breach</div>
          <div className="text-[16px] font-bold text-[#FF3B30]">{traders.filter((t) => t.status === 'breach').length}</div>
        </div>
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Safe</div>
          <div className="text-[16px] font-bold text-[#34C759]">{traders.filter((t) => t.status === 'safe').length}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="px-4 pt-3 flex items-center gap-1.5 overflow-x-auto">
        {(['all', 'breach', 'safe'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`cfilter-${f}`}
            className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
              filter === f ? 'bg-white text-black' : 'bg-white/10 text-white/70 active:opacity-40'
            ].join(' ')}
          >{f}</button>
        ))}
      </div>

      {/* Trader cards */}
      <div className="px-4 mt-3 space-y-3">
        {filtered.map((t) => {
          const maxDay = Math.max(...t.days.map((d) => d.pnl));
          const limitPct = t.phase === 'eval' ? 0.4 : 0.5;
          const limitDollars = t.target * limitPct;
          const breachDay = t.days.find((d) => d.pnl > limitDollars);
          return (
            <div key={t.id} data-testid={`consist-${t.id}`} className={['rounded-[16px] border bg-white/5 p-4',
              t.status === 'breach' ? 'border-[#FF3B30]/50' : 'border-white/10'
            ].join(' ')}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[14px] font-bold" style={{ backgroundColor: TIER_COLOR[t.tier] + '30', color: TIER_COLOR[t.tier] }}>{t.name[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[14px] font-bold text-white">{t.name}</span>
                    <span className={['rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase',
                      t.phase === 'eval' ? 'bg-[#FF9500]/20 text-[#FF9500]' : 'bg-[#34C759]/20 text-[#34C759]'
                    ].join(' ')}>{t.phase}</span>
                    {t.status === 'breach' && <span className="rounded-full bg-[#FF3B30] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">Breach</span>}
                  </div>
                  <div className="text-[11px] text-white/50 font-mono">{t.account} · ${(t.accountSize/1000).toFixed(0)}K · target ${t.target.toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-[12px] text-white/50">Limit</div>
                  <div className="text-[14px] font-bold text-white">${limitDollars.toLocaleString()}</div>
                </div>
              </div>

              {/* Bar chart */}
              <div className="mt-3 space-y-1.5">
                {t.days.map((d) => {
                  const pct = (d.pnl / Math.max(maxDay, t.target)) * 100;
                  const isBreach = d.pnl > limitDollars;
                  return (
                    <div key={d.date} data-testid={`day-${t.id}-${d.date}`} className="flex items-center gap-2">
                      <div className="w-10 text-[10px] text-white/50 tabular-nums">{d.date}</div>
                      <div className="flex-1 h-5 relative overflow-hidden rounded-[4px] bg-white/5">
                        {/* limit line */}
                        <div className="absolute top-0 bottom-0 w-px bg-[#FF9500]" style={{ left: `${(limitDollars / Math.max(maxDay, t.target)) * 100}%` }} />
                        <div className={['h-full rounded-[4px] transition-all',
                          isBreach ? 'bg-[#FF3B30]' : d.pnl > 0 ? 'bg-[#34C759]' : 'bg-[#8E8E93]'
                        ].join(' ')} style={{ width: `${Math.max(pct, 1)}%` }} />
                      </div>
                      <div className={['w-16 text-right text-[11px] tabular-nums', isBreach ? 'text-[#FF3B30] font-bold' : d.pnl > 0 ? 'text-white' : 'text-white/50'].join(' ')}>
                        {d.pnl >= 0 ? '+' : ''}${d.pnl.toLocaleString()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {breachDay && (
                <div className="mt-2 rounded-[8px] bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-2 text-[11px] text-[#FF3B30]">
                  ⚠ Day {breachDay.date}: ${breachDay.pnl.toLocaleString()} ({((breachDay.pnl / t.target) * 100).toFixed(0)}% of target). Excess ${(breachDay.pnl - limitDollars).toLocaleString()} added to target at 16:00 CT.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
